import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { useSiteContent } from "@/context/SiteContentContext";
import { toEmbedSrc, embedKind } from "@/lib/embeds";
import { fmtEventDate } from "@/lib/dates";
import EventsListHover from "@/components/EventsListHover";
import Seo from "@/components/Seo";

const fmt = (iso, country, lang) => {
    const { primary, secondary } = fmtEventDate(iso, country, lang);
    const parts = primary.split(/[\s,]+/).filter(Boolean);
    const isUS = /^[A-Z]{3}$/.test(parts[0]);
    if (isUS) return { day: parts[1] || "", month: parts[0] || "", year: parts[2] || "", time: secondary };
    return { day: parts[0] || "", month: parts[1] || "", year: parts[2] || "", time: secondary };
};

const kindClass = (k) => {
    switch (k) {
        case "vertical-video": return "aspect-[9/16]";
        case "video":          return "aspect-video";
        case "audio":          return "aspect-[16/6]";
        case "photo":
        default:               return "aspect-square";
    }
};

export default function Events() {
    const { t, lang } = useT();
    const { content: sc } = useSiteContent();
    const [upcoming, setUpcoming] = useState([]);
    const [past, setPast] = useState([]);

    useEffect(() => {
        api.get("/events", { params: { status: "upcoming" } }).then((r) => setUpcoming(r.data));
        api.get("/events", { params: { status: "past" } }).then((r) => setPast(r.data));
    }, []);

    // Page copy — editable in admin → site → events (página)
    const kicker = sc[`events_page_kicker_${lang}`] || sc.events_page_kicker_en || t("section_agenda");
    const title = sc[`events_page_title_${lang}`] || sc.events_page_title_en || t("nav_events");
    const intro = sc[`events_page_intro_${lang}`] || sc.events_page_intro_en || "";
    const upcomingLabel = sc[`events_upcoming_label_${lang}`] || sc.events_upcoming_label_en || (lang === "es" ? "próximos" : "upcoming");
    const pastLabel = sc[`events_past_label_${lang}`] || sc.events_past_label_en || (lang === "es" ? "pasados" : "past");

    return (
        <div data-testid="events-page" className="px-4 md:px-8 pt-14 md:pt-20 pb-14 md:pb-20">
            <Seo
                title={`${title || "Events"} | ITZKATU`}
                description={intro || "eventos, fiestas y apariciones del colectivo itzkatu — próximas fechas y archivo."}
                path="/events"
            />
            <div className="max-w-6xl mx-auto">
            {/* Page header */}
            <div className="mb-10 md:mb-14 space-y-4">
                {sc.events_page_show_kicker !== "0" && (
                    <div className="font-mono text-xs uppercase tracking-[0.3em] text-[color:var(--sc-accent)]">{kicker}</div>
                )}
                {sc.events_page_show_title !== "0" && (
                    <h1 className="itz-hero font-heading font-black uppercase tracking-tighter leading-[0.9]">
                        {String(title).replace(/\.$/, "")}<span className="text-[color:var(--sc-accent)]">.</span>
                    </h1>
                )}
                {sc.events_page_show_intro !== "0" && intro && (
                    <p className="font-body text-base md:text-lg leading-relaxed opacity-80 whitespace-pre-line max-w-2xl" data-testid="events-page-intro">
                        {intro}
                    </p>
                )}
            </div>

            {/* UPCOMING — list rows with visible flyer thumb + hover preview */}
            <section className="mb-20 md:mb-24" data-testid="events-upcoming-section">
                <div className="font-mono text-xs uppercase tracking-[0.3em] text-[color:var(--sc-accent)] mb-6">
                    {upcomingLabel}
                </div>
                {upcoming.length === 0 ? (
                    <div className="py-8 font-mono text-xs uppercase tracking-[0.3em] opacity-50">
                        {lang === "es" ? "nada por ahora — vuelve pronto." : "nothing here yet — check back soon."}
                    </div>
                ) : (
                    <EventsListHover events={upcoming} />
                )}
            </section>

            {/* PAST — media gallery */}
            {past.length > 0 && (
                <section data-testid="events-past-section">
                    <div className="font-mono text-xs uppercase tracking-[0.3em] opacity-50 mb-6">
                        {pastLabel}
                    </div>
                    <div className="space-y-16 md:space-y-24">
                        {past.map((e) => {
                            const d = fmt(e.date, e.country, lang);
                            const embeds = e.media_embeds || [];
                            const fallbackImg = e.flyer_url || e.image_url;
                            return (
                                <div key={e.id} data-testid={`past-event-${e.id}`} className="border-t border-current/10 pt-8 md:pt-10">
                                    <div className="grid grid-cols-12 gap-4 items-start mb-6 md:mb-8">
                                        <div className="col-span-3 md:col-span-2">
                                            <div className="font-heading font-black text-3xl md:text-5xl leading-none">{d.day}</div>
                                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] mt-1 opacity-60">{d.month} {d.year}</div>
                                        </div>
                                        <div className="col-span-9 md:col-span-10">
                                            <h2 className="font-heading font-black text-2xl md:text-4xl uppercase tracking-tight leading-none">{e.title}</h2>
                                            <div className="font-mono text-[11px] md:text-xs uppercase tracking-[0.2em] opacity-60 mt-2 flex items-center gap-2 flex-wrap">
                                                <MapPin size={12} /><span>{e.venue}</span><span>· {e.city}{e.country && `, ${e.country}`}</span>
                                            </div>
                                            {e.lineup?.length > 0 && (
                                                <div className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-60 mt-2">w/ {e.lineup.map((n, i) => {
                                                    return <span key={i}>{i > 0 && " · "}{n}</span>;
                                                })}</div>
                                            )}
                                            {e.description && (<p className="mt-4 font-body text-sm md:text-base opacity-80 max-w-2xl">{e.description}</p>)}
                                        </div>
                                    </div>
                                    {embeds.length > 0 ? (
                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                                            {embeds.map((url, idx) => {
                                                const kind = embedKind(url);
                                                const src = toEmbedSrc(url);
                                                return (
                                                    <div key={idx} data-testid={`past-media-${e.id}-${idx}`} className={`relative bg-black/40 overflow-hidden ${kindClass(kind)}`}>
                                                        <iframe src={src} title={`${e.title} media ${idx}`} loading="lazy" frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" allowFullScreen scrolling="no" className="absolute inset-0 w-full h-full" />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : fallbackImg ? (
                                        <div className="max-w-xs md:max-w-sm">
                                            <img src={fallbackImg} alt={e.title} className="w-full h-auto" data-testid={`past-img-${e.id}`} />
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {upcoming.length === 0 && past.length === 0 && (
                <div className="py-24 text-center font-mono text-xs uppercase tracking-[0.3em] opacity-50">
                    nothing here yet — check back soon.
                </div>
            )}
            </div>
        </div>
    );
}
